import { LitElement, html, css } from 'lit';
import { styleMap } from 'lit/directives/style-map.js';
import formCompleteBackgroundUrl from 'data-url:../static/images/form_background_waves_triple.svg';
import summitImageUrl from 'url:../static/images/form_image_summit_small_compressed.png';
import { modernNormalizeStyleSheet, globalStyleSheet, utilsStyleSheet } from './shared-styles.js';

// The displayed link (this.downloadLink) is intentionally scheme-less/www-less (see
// upload-container.js's getDisplayOrigin, which produces it), but a copied link should
// be a fully-qualified, directly-pasteable URL — re-add the page's own scheme (http/https)
// and a `www.` prefix. Duplicated from static/script/index.js, same precedent as
// normalizeFileName in form-upload.js/progress-div.js (components don't import from
// the page-level script).
const toClipboardLink = (displayLink) => `${window.location.protocol}//www.${displayLink}`;

export default class FormUploadCompleteElement extends LitElement {
    static properties = {
        downloadLink: {},
    };

    static styles = [modernNormalizeStyleSheet, globalStyleSheet, utilsStyleSheet, css`
        :host {
            display: block;
        }

        /* Without this, setting the \`hidden\` attribute/property has no visible effect:
           the UA stylesheet's [hidden] { display: none; } is a lower-priority origin
           than this component's own :host { display: block; } rule above, so origin
           order (author beats user-agent, regardless of specificity) lets :host win
           and the element stays visible even while hidden. */
        :host([hidden]) {
            display: none;
        }

        .upload-complete-form {
            align-items: center;
            animation: fadeIn 0.5s ease-out;
            background-color: var(--color-form-bg);
            /* background-image is set via styleMap() in render(), not here */
            background-position: bottom;
            border-radius: 20px;
            box-shadow: var(--shadow-input);
            display: flex;
        }

        .upload-complete-form__right-side {
            flex-grow: 1;
        }

        .upload-complete-form__copy-area {
            align-items: center;
            background-color: var(--color-input-bg);
            border-radius: 10px;
            box-shadow: var(--shadow-input);
            display: flex;
            justify-content: space-between;
            overflow: hidden;
            padding-inline: 0.5rem;
        }

        .upload-complete-form__text-input {
            color: var(--color-link-active);
            cursor: pointer;
            flex-grow: 1;
            overflow: hidden;
            padding: 1rem;
            text-decoration: underline;
            text-decoration-thickness: 1px;
            text-overflow: ellipsis;
            text-underline-offset: 5px;
            text-wrap: nowrap;
        }

        .upload-complete-form__copy-button {
            align-items: center;
            border-radius: 50%;
            cursor: pointer;
            display: flex;
            font-size: 1.5rem;
            height: 35px;
            justify-content: center;
            width: 35px;
            /* transition: all 0.3s */ /* When 'all' is used, during the initial phases of color transition, some pink color shown. FIXME */
        }

        .upload-complete-form__upload-another{
            display: block;
            width: 12em;
        }

        /* also .upload-complete-form__copy-button:hover path
           selector works too
         */
        .upload-complete-form__copy-button:hover {
            color: var(--color-input-primary);
            fill: currentColor;
        }

        /* xs */
        @media screen and (min-width: 320px) {
            .upload-complete-form {
                flex-direction: column;
                padding-block-end: 20px;
            }

            .upload-complete-form__image {
                height: 120px;
                margin-bottom: 1rem;
                width: 120px;
            }

            .upload-complete-form__right-side {
                width: 100%;
            }
        }

        /* md */
        @media screen and (min-width: 448px) {
            .upload-complete-form__image {
                height: 150px;
                margin-bottom: 0;
                width: 150px;
            }

            .upload-complete-form__info {
                font-size: 1rem;
            }

            .upload-complete-form__text-input {
                font-size: 1rem;
            }
        }

        /* xl */
        @media screen and (min-width: 576px) {
            .upload-complete-form__image {
                height: 180px;
                width: 180px;
            }

            .upload-complete-form__info {
                font-size: 1.25rem;
            }

            .upload-complete-form__text-input {
                font-size: 1.25rem;
            }
        }

        /* 3xl */
        @media screen and (min-width: 768px) {
            .upload-complete-form {
                flex-direction: row;
                padding-block-start: 20px;
            }

            .upload-complete-form__info {
                font-size: 1.5rem;
            }

            .upload-complete-form__text-input {
                font-size: 1.5rem;
            }
        }

        /* 5xl */
        @media screen and (min-width: 1024px) {
            .upload-complete-form {
                padding-block-end: 0px;
                padding-block-start: 0px;
            }

            .upload-complete-form__image {
                height: 230px;
                width: 230px;
            }

            .upload-complete-form__copy-area {
                max-width: 576px;
            }
        }

        @media (prefers-reduced-motion: reduce) {
            .upload-complete-form {
                animation: none;
            }
        }
    `];

    // Default assigned in the constructor, not as a class field: a class field would
    // define an own property via [[DefineOwnProperty]] that shadows the reactive
    // accessor Lit generates from `static properties`, silently breaking updates
    // (see _plans/4.3-lit_progress_div_component.md Step 8 for the bug this avoids).
    constructor() {
        super();
        this.downloadLink = 'tempup.io/file/ABCD1234';
    }

    // Native :focus-visible can't tell Tab-focus apart from click/programmatic focus
    // for text inputs (browsers intentionally match it on click for those too). Mark
    // the input Tab lands on so CSS can show the outline only for that case.
    // Tab's keydown fires on the element about to lose focus; by keyup, focus has
    // already moved, so e.target here is the newly-focused element.
    #handleTextInputTabKeyUp(e) {
        if (e.key === 'Tab') {
            e.target.classList.add('tab-pressed');
        }
    }

    // Using blur (not focusout) clears the marker correctly when focus round-trips
    // through a native OS dialog (e.g. the file picker) and returns to the same input.
    #handleTextInputBlur(e) {
        e.target.classList.remove('tab-pressed');
    }

    // input[type="text"] sits inside a <form> with no type="submit" button. Per the HTML
    // implicit-submission rule, a form with no submit button still submits on Enter if it
    // has exactly one single-line text field — which is the shape of this form — reloading
    // the page. Space has its own default (page scroll). Prevent both defaults and instead
    // fire the same `click` this element already responds to, so keyboard activation
    // matches mouse activation exactly. `e.repeat` guards against auto-repeat firing click
    // on every repeated keydown while the key is held, matching native single-activation-
    // per-press button behavior.
    #handleKeyboardActivate(e) {
        if ((e.key === 'Enter' || e.key === ' ') && !e.repeat) {
            e.preventDefault();
            e.target.click();
        }
    }

    // Mirrors FormUploadElement's #handleSubmitClick (form-upload plan Step 13):
    // this component doesn't hide itself — it announces the click and lets an
    // ancestor (e.g. <upload-container>) own cross-component coordination.
    #handleUploadAnotherClick() {
        this.dispatchEvent(new CustomEvent('upload-another-clicked', { bubbles: true, composed: true }));
    }

    // Public: called by an ancestor (e.g. <upload-container>) once this component
    // is shown, to move focus onto the download-link input — mirrors
    // static/script/index.js's finishUpload calling downloadLinkInput.focus()
    // right after revealing #form-upload-complete.
    focusDownloadLink() {
        this.renderRoot.getElementById('input-download-link').focus();
    }

    // Mirrors static/script/index.js's copyDownloadLink, bound to both #input-download-link
    // and #button-copy (same two listeners the original attaches). TODO: showToast()'s
    // equivalent isn't called here yet — no toast component exists in this migration yet.
    async #copyDownloadLink() {
        if (!this.downloadLink) {
            return;
        }

        // TODO: Remove this
        // It's check for fallback support of the Clipboard API, which is used in modern browsers.
        if (!navigator.clipboard || typeof navigator.clipboard.writeText !== 'function') {
            return;
        }

        await navigator.clipboard.writeText(toClipboardLink(this.downloadLink));

        const downloadLinkInput = this.renderRoot.getElementById('input-download-link');
        downloadLinkInput.focus();
        downloadLinkInput.select();
        downloadLinkInput.setSelectionRange(0, this.downloadLink.length);
        this.dispatchEvent(new CustomEvent('download-link-copied', { bubbles: true, composed: true }));
    }

    render() {
        // background-image for .upload-complete-form, kept out of static styles css since Parcel can't resolve data-url: inside it
        // alt text of upload-complete-form__image is empty because the image is decorative
        return html`
            <form id="form-upload-complete" class="container-padding upload-complete-form" autocomplete="off" style=${styleMap({ backgroundImage: `url(${formCompleteBackgroundUrl})` })}>
                <img class="upload-complete-form__image" src="${summitImageUrl}" alt="" width="230" height="230"/>
                <div class="upload-complete-form__right-side">
                    <p class="upload-complete-form__info">Your download link is ready:</p>
                    <div class="upload-complete-form__copy-area icon--hoverable mt-1rem">
                        <label class="visually-hidden" for="input-download-link">Download link: </label>
                        <input id="input-download-link" type="text" class="upload-complete-form__text-input" value="${this.downloadLink}" readonly @click=${this.#copyDownloadLink} @keyup=${this.#handleTextInputTabKeyUp} @blur=${this.#handleTextInputBlur} @keydown=${this.#handleKeyboardActivate}/>
                        <i id="button-copy" class="upload-complete-form__copy-button icon icon--copy" title="Copy to clipboard" aria-hidden="true" @click=${this.#copyDownloadLink}></i>
                    </div>
                    <button id="button-upload-another" class="upload-complete-form__upload-another mt-1rem mi-auto" type="button" @click=${this.#handleUploadAnotherClick}>
                        Upload Another
                        <i class="icon icon--rotate-right" aria-hidden="true"></i>
                    </button>
                </div>
            </form>
        `;
    }
}

customElements.define('tempup-form-upload-complete', FormUploadCompleteElement);
